// src/pages/PoliciesPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { request } from '../utils/request';
import ReusableHeader from '../components/Header/ReusableHeader';

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const fetchPolicies = async () => {
    try {
      const data = await request('policies', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      // Map Mongo _id → id for convenience
      const normalized = arr.map((r) => ({
        id: r._id,
        name: r.name,
        description: r.description,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }));
      setPolicies(normalized);
    } catch (err) {
      console.error('Error fetching Policies:', err);
      setPolicies([]);
      showSnack('Failed to fetch policies.', 'error');
    }
  };
  const handleOpen = (policy) => {
    if (policy) {
      setFormData(policy);
      setIsEditing(true);
    } else {
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: formData }
        : { method: 'post', body: formData };
      await request('policies', opts);
      await fetchPolicies();
      handleClose();
      showSnack(isEditing ? 'Policy updated successfully.' : 'Policy created successfully.', 'success');
    } catch (err) {
      console.error('Error saving policy:', err);
      showSnack('Failed to save policy.', 'error');
    }
  };

  // Delete handlers
  const handleDeleteClick = (policy) => {
    setDeletingPolicy(policy);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingPolicy(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPolicy || !deletingPolicy.id) return;
    setDeleting(true);
    try {
      await request('policies', { method: 'delete', params: deletingPolicy.id });
      await fetchPolicies();
      showSnack(`Policy "${deletingPolicy.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete policy:', err);
      showSnack('Failed to delete policy.', 'error');
      setDeleting(false);
    }
  };

  return (
    <Box p={2}>
      {/* Header */}
      <ReusableHeader
        title="Policies"
        breadcrumbs={[{ label: "Policies" }]}
        actionButton={
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: '#2d9cdb',
              '&:hover': { backgroundColor: '#0b1d51' },
              py: 0.5,
              px: 2,
            }}
          >
            Add Policy
          </Button>
        }
      />

      {/* Policies Table */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f0f7f5' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                {/* <TableCell>Description</TableCell> */}
                <TableCell>Created At</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((p, idx) => (
                <TableRow key={p.id || idx} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  {/* <TableCell>{p.description || '—'}</TableCell> */}
                  <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(p)}
                      sx={{ color: '#2d9cdb', p: 0.5 }}
                      aria-label={`edit-policy-${p.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(p)}
                      sx={{ color: '#e53935', p: 0.5, ml: 1 }}
                      aria-label={`delete-policy-${p.id}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {policies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No policies found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ backgroundColor: '#2d9cdb', color: '#fff', fontSize: '1rem', py: 1 }}
        >
          {isEditing ? 'Edit Policy' : 'Add Policy'}
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: '10px' }}>
          <Stack spacing={1}>
            <TextField
              size="small"
              label="Policy Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              size="small"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#2d9cdb' } }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-policy-dialog-title"
      >
        <DialogTitle id="delete-policy-dialog-title">Delete Policy</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the policy{' '}
            <strong>{deletingPolicy ? deletingPolicy.name : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
